/**
 * THE ATTACH DIALOG — `tasks/practice-documents.md` §14.3, §14.4.
 *
 * The searchable, tickable list that used to be rendered inline by the picker. The owner's shape:
 *
 *   *"a chip list prefilled with the items linked to appointment sending via trigger, or linked to
 *   an invite etc. Then it has an attach icon that opens a dialog with the group's documents as a
 *   tickable and filterable/searchable list."*
 *
 * ── IT WORKS ON A COPY ───────────────────────────────────────────────────────────────────────
 *
 * `data.selection` is copied on the way in and only leaves on OK. Cancel — and Escape, and the
 * backdrop — close with `undefined`, which a host must read as *"leave the selection alone"*
 * rather than as an empty selection. A dialog that mutated the host's array as boxes were ticked
 * would make Cancel a lie, and would do it invisibly.
 *
 * ── IT LOADS NOTHING ─────────────────────────────────────────────────────────────────────────
 *
 * The rows come in on `MAT_DIALOG_DATA`. Two loaders — one for the chip row, one for the dialog —
 * is how the chip row and the dialog would come to disagree about what the practice has, and §14.1
 * records that exact duplication happening once already with `sendSheet`.
 * `DocumentAttachmentService.choose` is the loader, for hosts that have no chip row.
 *
 * ── WHAT IT KEEPS FROM THE INLINE LIST, AND WHY ──────────────────────────────────────────────
 *
 * The hidden-by-search counter, the per-document `requiresResponse` sentence, and the
 * whole-selection consequence all move here unchanged. The counter in particular: a ticked
 * document the current search hides is STILL GOING OUT, and a user who cannot see that is a user
 * who does not know what they are about to send.
 */

import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { PracticeDocumentDto } from '../../../core/services/practice-document.service';
import {
  requiresResponse as documentRequiresResponse,
  responseModeIcon,
  responseModeSentence,
  selectionConsequence,
} from '../../../group/components/practice-document-settings/practice-document-copy';
import {
  DocumentAttachmentDialogData,
  DocumentAttachmentDialogResult,
  DocumentAttachmentTriggerMap,
  emptyMessage,
} from './document-attachment';

@Component({
  selector: 'app-document-attachment-dialog',
  templateUrl: './document-attachment-dialog.component.html',
  styleUrls: ['./document-attachment-dialog.component.scss'],
})
export class DocumentAttachmentDialogComponent {

  /** Filters by name AND category, because a practice files by both. */
  search = '';

  /** The working copy. `data.selection` is never touched. */
  selection: string[];

  readonly documents: PracticeDocumentDto[];
  readonly triggers: DocumentAttachmentTriggerMap;

  constructor(
    private dialogRef: MatDialogRef<DocumentAttachmentDialogComponent,
      DocumentAttachmentDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentAttachmentDialogData,
  ) {
    this.documents = (data && data.documents) ? data.documents : [];
    this.triggers = (data && data.triggers) ? data.triggers : {};
    this.selection = (data && data.selection) ? data.selection.slice() : [];
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Ticking
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  isSelected(document: PracticeDocumentDto): boolean {
    return this.selection.indexOf(document.id) !== -1;
  }

  toggle(document: PracticeDocumentDto): void {
    this.selection = this.isSelected(document)
      ? this.selection.filter((id) => id !== document.id)
      : this.selection.concat([document.id]);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Searching, and what is on screen
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Name AND category (§14.3). A practice that files by "Admission" and one that files by "SAQ"
   * both find it, and a search that matched only the name would make the category a decoration.
   */
  get visibleDocuments(): PracticeDocumentDto[] {
    const term = (this.search || '').trim().toLowerCase();
    if (!term) return this.documents;
    return this.documents.filter((document) => {
      const name = (document.name || '').toLowerCase();
      const category = (document.category || '').toLowerCase();
      return name.indexOf(term) !== -1 || category.indexOf(term) !== -1;
    });
  }

  /**
   * The chosen rows, from the WHOLE list rather than the filtered one.
   *
   * A search narrows what is on screen; it does not untick anything. The consequence sentence has
   * to describe what will actually be sent, and a summary that changed as the user typed would be
   * a lie about the send.
   */
  get selectedDocuments(): PracticeDocumentDto[] {
    return this.documents.filter((document) => this.selection.indexOf(document.id) !== -1);
  }

  get selectedCount(): number {
    return this.selectedDocuments.length;
  }

  /** Hidden by the search but still going out — otherwise the count reads as a bug. */
  get hiddenSelectedCount(): number {
    const visible = this.visibleDocuments;
    return this.selectedDocuments.filter((document) => visible.indexOf(document) === -1).length;
  }

  /** §14.4, in the settings tab's register. `null` for an empty selection. */
  get consequence(): string | null {
    return selectionConsequence(this.selectedDocuments);
  }

  /** Per document — the thing that changes what the patient receives (§14.2). */
  responseModeSentence(document: PracticeDocumentDto): string {
    return responseModeSentence(document);
  }

  responseModeIcon(document: PracticeDocumentDto): string {
    return responseModeIcon(document);
  }

  requiresResponse(document: PracticeDocumentDto): boolean {
    return documentRequiresResponse(document);
  }

  /** *"3 days before the appointment"*, where a trigger put this document on offer. */
  trigger(document: PracticeDocumentDto): string | null {
    return this.triggers[document.id] || null;
  }

  /**
   * A practice with nothing to attach.
   *
   * The chip row HIDES ITSELF in this case, so a user only reaches this through
   * `DocumentAttachmentService.choose` — a menu item, where something has to open. The sentence
   * says what to do about it rather than leaving an empty box.
   */
  get isEmpty(): boolean {
    return this.documents.length === 0;
  }

  get emptyMessage(): string {
    return emptyMessage(this.data ? this.data.context : 'manual');
  }

  /** Everything is filtered out by the search, but the practice does have documents. */
  get isFilteredEmpty(): boolean {
    return this.documents.length > 0 && this.visibleDocuments.length === 0;
  }

  trackById(_index: number, document: PracticeDocumentDto): string {
    return document.id;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Leaving
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  confirm(): void {
    this.dialogRef.close({
      selection: this.selection.slice(),
      documents: this.selectedDocuments,
    });
  }

  /** `undefined`, which is what Escape and the backdrop close with too. */
  cancel(): void {
    this.dialogRef.close();
  }
}
